import styled from 'styled-components';

export type ActivityBadgeVariant = 'email' | 'comment' | 'status' | 'system';

interface TypeBadge {
	label: string;
	variant: ActivityBadgeVariant;
}

interface Props {
	avatarInitial: string;
	typeBadge: TypeBadge;
	message: string;
	actorName: string;
	timestamp: string;
}

// Tailwind palette ports, keyed by badge variant. Avatar uses the *-100/-700
// pair; the badge pill uses the *-50/-700 pair plus a *-200 border.
const VARIANT_COLORS: Record<
	ActivityBadgeVariant,
	{avatarBg: string; avatarText: string; badgeBg: string; badgeText: string; badgeBorder: string}
> = {
	email: {avatarBg: '#dbeafe', avatarText: '#1d4ed8', badgeBg: '#eff6ff', badgeText: '#1d4ed8', badgeBorder: '#bfdbfe'},
	comment: {avatarBg: '#dcfce7', avatarText: '#15803d', badgeBg: '#f0fdf4', badgeText: '#15803d', badgeBorder: '#bbf7d0'},
	status: {avatarBg: '#f1f5f9', avatarText: '#475569', badgeBg: '#fefce8', badgeText: '#a16207', badgeBorder: '#fef08a'},
	system: {avatarBg: '#f1f5f9', avatarText: '#475569', badgeBg: '#f8fafc', badgeText: '#475569', badgeBorder: '#e2e8f0'},
};

function formatRelativeTime(iso: string): string {
	const date = new Date(iso);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	if (diffMins < 1) return 'just now';
	if (diffMins < 60) return `${diffMins}m ago`;
	const diffHours = Math.floor(diffMins / 60);
	if (diffHours < 24) return `${diffHours}h ago`;
	const diffDays = Math.floor(diffHours / 24);
	if (diffDays < 7) return `${diffDays}d ago`;
	return date.toLocaleDateString();
}

const Row = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 12px 0;
`;

const Avatar = styled.div<{$variant: ActivityBadgeVariant}>`
	display: flex;
	height: 32px;
	width: 32px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: ${p => p.theme.ticketing.radius.full};
	font: ${p => p.theme.ticketing.font.body3};
	font-weight: 600;
	background: ${p => VARIANT_COLORS[p.$variant].avatarBg};
	color: ${p => VARIANT_COLORS[p.$variant].avatarText};
`;

const Main = styled.div`
	min-width: 0;
	flex: 1 1 0%;
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const ActorName = styled.span`
	font: ${p => p.theme.ticketing.font.subheading2};
	font-weight: 600;
	color: #0f172a;
`;

const Badge = styled.span<{$variant: ActivityBadgeVariant}>`
	border-radius: 4px;
	padding: 2px 6px;
	font: ${p => p.theme.ticketing.font.overline};
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.025em;
	background: ${p => VARIANT_COLORS[p.$variant].badgeBg};
	color: ${p => VARIANT_COLORS[p.$variant].badgeText};
	border: 1px solid ${p => VARIANT_COLORS[p.$variant].badgeBorder};
`;

const Timestamp = styled.span`
	margin-left: auto;
	font: ${p => p.theme.ticketing.font.body3};
	color: #94a3b8;
`;

const Message = styled.p`
	margin-top: 4px;
	font: ${p => p.theme.ticketing.font.subheading2};
	color: #475569;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
`;

export function RecentActivityEntry({avatarInitial, typeBadge, message, actorName, timestamp}: Props) {
	return (
		<Row>
			<Avatar data-testid="activity-avatar" $variant={typeBadge.variant}>
				{avatarInitial}
			</Avatar>
			<Main>
				<Header>
					<ActorName>{actorName}</ActorName>
					<Badge $variant={typeBadge.variant}>{typeBadge.label}</Badge>
					<Timestamp>{formatRelativeTime(timestamp)}</Timestamp>
				</Header>
				<Message>{message}</Message>
			</Main>
		</Row>
	);
}
